import { useParams } from "react-router";

import useMeetingReportMetricsQuery from "@app/analytics/MeetingReport/hooks/useMeetingReportMetricsQuery";
import { PROD_SAMPLE_MEETING } from "@app/analytics/sampleData/sampleMeetings";
import {
  ParsedQueryResultRequiredBy,
  parseMetricsDataFromQueryResult,
  parseMetricsDataListFromQueryResult,
} from "@app/analytics/utils";

import { SAMPLE_COACHING_CHARISMA_BIAS } from "./sampleData";

type ParticipantQueryData = ParsedQueryResultRequiredBy<
  | "participantId"
  | "participantName"
  | "participantBias"
  | "participantCharisma"
  | "participantTalkTime"
  | "talkTimePercent"
>;

type MyQueryData = ParsedQueryResultRequiredBy<
  "participantBias" | "participantCharisma"
>;

export interface CharismaBiasParticipant {
  id: string;
  name: string;
  talkTime: string;
  talkTimePercent: string;
  bias: number | null;
  charisma: number | null;
}

const createParticipantsFromMetricsData = (
  data: ParticipantQueryData[]
): CharismaBiasParticipant[] =>
  data.map((d) => {
    return {
      id: d.participantId ?? `no_user_${d.participantName}`,
      name: d.participantName!,
      talkTime: d.participantTalkTime.formattedValue,
      talkTimePercent: d.talkTimePercent.formattedValue,
      bias: d.participantBias.value,
      charisma: d.participantCharisma.value,
    };
  });

const DEFAULT_RESULT: MyQueryData = {
  participantBias: { value: null, formattedValue: "-" },
  participantCharisma: { value: null, formattedValue: "-" },
};

const useCharismaAndBias = () => {
  const { sessionId } = useParams();
  const isSampleReport = sessionId === PROD_SAMPLE_MEETING.id;

  const myMetricsQuery = useMeetingReportMetricsQuery(
    {
      metrics: ["participantBias", "participantCharisma"],
      people: "everyone_but_me",
    },
    {
      queryKey: ["coaching", "charismaAndBias", "me"],
      enabled: !isSampleReport,
    }
  );

  const participantMetricsQuery = useMeetingReportMetricsQuery(
    {
      metrics: [
        "participantBias",
        "participantCharisma",
        "participantTalkTime",
        "talkTimePercent",
      ],
      dimensions: ["participantName", "participantId"],
    },
    {
      queryKey: ["coaching", "charismaAndBias", "participants"],
      enabled: !isSampleReport,
    }
  );

  const [participantMetricsData] =
    parseMetricsDataListFromQueryResult<ParticipantQueryData>(
      participantMetricsQuery.data
    );

  const [myData] = parseMetricsDataFromQueryResult<MyQueryData>(
    myMetricsQuery.data,
    DEFAULT_RESULT
  );

  const refetch = () => {
    if (myMetricsQuery.isError) myMetricsQuery.refetch();
    if (participantMetricsQuery.isError) participantMetricsQuery.refetch();
  };

  const data = {
    my: {
      bias: myData?.participantBias.value,
      charisma: myData?.participantCharisma.value,
    },
    participants: createParticipantsFromMetricsData(
      participantMetricsData ?? []
    ),
  };

  return {
    data: isSampleReport ? SAMPLE_COACHING_CHARISMA_BIAS : data,
    isError: myMetricsQuery.isError || participantMetricsQuery.isError,
    isInitialLoading:
      myMetricsQuery.isLoading || participantMetricsQuery.isLoading,
    refetch,
  };
};

export default useCharismaAndBias;
